import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";

import { PayoutAccount } from "../RequestPayouts";

interface RequestPayoutProps {
  open: boolean;
  onClose: () => void;
  availableBalance: number;
  accounts: PayoutAccount[];
  selectedAccount: PayoutAccount | null;
  onSelectAccount: (account: PayoutAccount) => void;
  amount: string;
  onAmountChange: (val: string) => void;
  onAddAccount: () => void;
  onConfirm: () => void;
}

export default function RequestPayout({
  open,
  onClose,
  availableBalance,

  selectedAccount,
  amount,
  onAmountChange,
  onAddAccount,
  onConfirm,
}: RequestPayoutProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const parsedAmount = parseFloat(amount);
  const amountError =
    amount !== "" && (isNaN(parsedAmount) || parsedAmount <= 0)
      ? "Enter a valid amount."
      : parsedAmount > availableBalance
        ? "Amount exceeds available balance."
        : null;

  const canConfirm =
    !amountError && parsedAmount > 0 && selectedAccount !== null;
  function methodDisplayName(acct: PayoutAccount): string {
    const METHOD_LABELS: Record<string, string> = {
      ECOCASH: "Ecocash",
      INNBUCKS: "InnBucks",
      OMARI: "Omari",
      ONEMONEY: "OneMoney",
    };
    if (acct.method === "BANK") return acct.bank_name ?? "Bank Transfer";
    return METHOD_LABELS[acct.method] ?? acct.method;
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          m: 2,
          maxWidth: 360,
          width: "100%",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: reduxTheme.primary_colors?.[400],
                fontWeight: 600,
                fontSize: "18px",
              }}
            >
              Request Payout
            </Typography>
            <IconButton
              sx={{
                color: reduxTheme.primary_colors?.[400],
                height: "24px",
                width: "24px",
              }}
              onClick={onClose}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Available Balance Card */}
          <Box
            sx={{
              background: reduxTheme.primary_colors?.[800],
              borderRadius: 3,
              p: 2,
              textAlign: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "10px", display: "block", mb: 0.5 }}
            >
              Available balance
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "16px" }}>
              ${availableBalance.toFixed(2)}
            </Typography>
          </Box>

          {/* Amount Field */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", mb: 1, fontWeight: 500 }}
            >
              Amount<span style={{ color: "#c00" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="0.00"
              error={!!amountError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#ccc" },
                  "&.Mui-focused fieldset": {
                    borderColor: reduxTheme.primary_colors?.[400],
                  },
                },
              }}
            />
            {amountError && (
              <Alert
                severity="error"
                sx={{ mt: 1, py: 0, borderRadius: 2, fontSize: "12px" }}
              >
                {amountError}
              </Alert>
            )}
          </Box>

          {/* Payout Account Selector */}
          {/* Selected Payout Account */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", mb: 1, fontWeight: 500 }}
            >
              Method
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${color400}`,
                bgcolor: `${color400}10`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "13px" }}
              >
                {selectedAccount
                  ? methodDisplayName(selectedAccount)
                  : "No method selected"}
              </Typography>
              <Button
                size="small"
                onClick={onAddAccount}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  color: color400,
                  p: 0,
                  minWidth: 0,
                }}
              >
                Change
              </Button>
            </Box>
          </Box>
          {/* Confirm Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={!canConfirm}
            sx={{
              background: canConfirm
                ? `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`
                : undefined,
              color: reduxTheme.primary_colors?.[900],
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            Review Request
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
